/**
 * Shadow-DOM-piercing query + observation helpers.
 *
 * The Relewise predictive overlay (`<relewise-product-search-overlay>`) is
 * injected at runtime by the Relewise library, and its search input and result
 * tiles (`<relewise-product-search-overlay-product>`) live in library-controlled
 * (and nested) shadow roots. A plain `querySelector`/`querySelectorAll` does NOT
 * pierce shadow boundaries, so a light-tree-only read sees nothing. These
 * helpers walk every shadow root they encounter so reads and mutation
 * observation work across the whole light + shadow tree.
 */

/**
 * Like `querySelectorAll`, but recurses into every `shadowRoot` it encounters
 * and returns matches across both the light tree and all (nested) shadow trees.
 *
 * Returns a flat array of `HTMLElement`. Non-HTMLElement matches (e.g. SVG) are
 * filtered out so callers can rely on `.value`, `.tagName`, etc.
 */
export const deepQuerySelectorAll = (
  root: Element | Document | ShadowRoot,
  selector: string
): HTMLElement[] => {
  const matches: HTMLElement[] = [];

  const visit = (node: Element | Document | ShadowRoot) => {
    // Direct matches in this tree.
    node.querySelectorAll(selector).forEach((el) => {
      if (el instanceof HTMLElement) {
        matches.push(el);
      }
    });

    // Recurse into every shadow root hosted under this tree. Elements with an
    // open shadowRoot expose it; closed roots are unreachable by design.
    node.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) {
        visit(el.shadowRoot);
      }
    });

    // The starting node may itself be a shadow host (e.g. the overlay host
    // passed directly in), in which case its own shadowRoot is not found by the
    // descendant scan above.
    if (node instanceof Element && node.shadowRoot) {
      visit(node.shadowRoot);
    }
  };

  visit(root);

  // De-dupe: a host passed directly in can have its shadowRoot visited twice via
  // the two branches above.
  return Array.from(new Set(matches));
};

/**
 * Attach a `MutationObserver` to `root` and recursively to every `shadowRoot`
 * under it, calling `onMutation` on any childList/subtree change.
 *
 * Crucially, a shadow host may not have its `shadowRoot` (or its children) at
 * the moment we first attach - the Relewise library attaches roots and mounts
 * tiles asynchronously. So on every mutation we re-scan for newly-appearing
 * shadow hosts and attach to their roots too. This is what makes nested-shadow
 * tile additions actually trigger `onMutation`.
 */
export const observeDeep = (
  root: HTMLElement,
  onMutation: () => void
): void => {
  const observed = new WeakSet<Node>();

  const attach = (node: Element | ShadowRoot) => {
    if (observed.has(node)) return;
    observed.add(node);
    observer.observe(node, { childList: true, subtree: true });
  };

  const attachAllShadowRoots = (node: Element | ShadowRoot) => {
    // The node itself may be (or host) a shadow root.
    if (node instanceof Element && node.shadowRoot) {
      attach(node.shadowRoot);
      attachAllShadowRoots(node.shadowRoot);
    }
    node.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) {
        attach(el.shadowRoot);
        attachAllShadowRoots(el.shadowRoot);
      }
    });
  };

  const observer = new MutationObserver(() => {
    // New shadow hosts can appear on any mutation; (re-)attach before notifying.
    attachAllShadowRoots(root);
    onMutation();
  });

  attach(root);
  attachAllShadowRoots(root);
};
